import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import {
  Plus,
  Edit,
  Trash,
  ChevronLeft,
  ChevronRight,
  X,
  Package,
  DollarSign,
  Tag,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Star,
  TrendingUp,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Sparkles,
  Shield,
  Award,
  Zap,
  Globe,
  Heart,
  ArrowRight,
  Settings,
  BarChart3,
  Activity,
  Bell,
  Info,
  Ruler,
  Layers,
  Target,
  Maximize,
  Minimize
} from "lucide-react";

const SizesManagement = () => {
  const [sizes, setSizes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [formData, setFormData] = useState({
    name: "",
    label: "",
    price: "",
    product_id: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(0);

  // Vérifier l'authentification
  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: { pathname: "/dashboard-ecobag-secure/sizes" } } });
    }
  }, [user, navigate]);

  // Load sizes and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setIsVisible(true);

        // Load sizes and products in parallel
        const [sizesResponse, productsResponse] = await Promise.all([
          axios.get("https://api.greenecobagsusa.com/api/sizes"),
          axios.get("https://api.greenecobagsusa.com/api/products"),
        ]);

        setSizes(Array.isArray(sizesResponse.data) ? sizesResponse.data : []);

        // Handle different possible response formats for products correctly
        let productsData = [];
        if (productsResponse.data && productsResponse.data.data && Array.isArray(productsResponse.data.data)) {
          // Format { data: [...] }
          productsData = productsResponse.data.data;
        } else if (Array.isArray(productsResponse.data)) {
          // Format direct array
          productsData = productsResponse.data;
        } else if (productsResponse.data && typeof productsResponse.data === 'object') {
          // Format object with values to extract
          productsData = Object.values(productsResponse.data);
        }

        setProducts(productsData);
        setTotalPages(Math.ceil(filteredSizes.length / itemsPerPage)); setError("");
      } catch (err) {
        console.error("Error loading data:", err);
        setError(
          "Unable to load data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [itemsPerPage]);


  // Réinitialiser la pagination lors de la recherche
  useEffect(() => {
    setCurrentPage(1);
    const filtered = sizes.filter((size) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        size.name.toLowerCase().includes(searchLower) ||
        size.label.toLowerCase().includes(searchLower) ||
        size.price.toString().includes(searchLower) ||
        getProductName(size.product_id).toLowerCase().includes(searchLower)
      );
    });
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  }, [searchTerm, sizes, itemsPerPage]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for price field
    if (name === "price" && parseFloat(value) <= 0 && value !== "") {
      setFormErrors({
        ...formErrors,
        price: "Price must be greater than 0.00$"
      });
    } else {
      // Reset error for this field
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null,
        });
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const openModal = (size = null) => {
    // Check if products is an array and contains elements
    if (!Array.isArray(products) || products.length === 0) {
      setError("No products are available to create sizes.");
      return;
    }

    if (size) {
      // Mode édition
      setSelectedSize(size);
      setFormData({
        name: size.name || "",
        label: size.label || "",
        price: size.price ? size.price.toString() : "",
        product_id: size.product_id ? size.product_id.toString() : products[0].id.toString(),
      });
    } else {
      // Mode création - s'assurer que le premier produit existe
      const defaultProductId = products[0] && products[0].id ? products[0].id.toString() : "";

      // Mode création
      setSelectedSize(null);
      setFormData({
        name: "",
        label: "",
        price: "",
        product_id: defaultProductId,
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSize(null);
    setFormData({
      name: "",
      label: "",
      price: "",
      product_id: "",
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    // Validate price is greater than 0
    if (parseFloat(formData.price) <= 0) {
      setFormErrors({
        ...formErrors,
        price: "Price must be greater than 0.00$"
      });
      return; // Stop form submission
    }

    try {
      let response;

      // Prepare form data with exact price value (as string to preserve decimal places)
      const submitData = {
        ...formData,
        price: formData.price // Keep as string to preserve exact decimal places
      };

      if (selectedSize) {
        // Mode édition
        response = await axios.put(
          `https://api.greenecobagsusa.com/api/sizes/${selectedSize.id}`,
          submitData
        );
      } else {
        // Mode création
        response = await axios.post(
          "https://api.greenecobagsusa.com/api/sizes",
          submitData
        );
      }

      closeModal();

      // Recharger les tailles
      const sizesResponse = await axios.get("https://api.greenecobagsusa.com/api/sizes");
      setSizes(Array.isArray(sizesResponse.data) ? sizesResponse.data : []);
      setTotalPages(Math.ceil((Array.isArray(sizesResponse.data) ? sizesResponse.data.length : 0) / itemsPerPage));
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);

      if (error.response && error.response.status === 422) {
        setFormErrors(error.response.data.errors || {});
      } else {
        setError(
          "Une erreur est survenue lors de l'enregistrement de la taille."
        );
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette taille?")) {
      try {
        await axios.delete(`https://api.greenecobagsusa.com/api/sizes/${id}`);

        // Recharger les tailles
        const sizesResponse = await axios.get(
          "https://api.greenecobagsusa.com/api/sizes"
        );
        setSizes(Array.isArray(sizesResponse.data) ? sizesResponse.data : []);
        setTotalPages(Math.ceil((Array.isArray(sizesResponse.data) ? sizesResponse.data.length : 0) / itemsPerPage));

        // Ajuster la page courante si nécessaire
        if (
          currentPage > Math.ceil((Array.isArray(sizesResponse.data) ? sizesResponse.data.length : 0) / itemsPerPage) &&
          currentPage > 1
        ) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        setError(
          "Impossible de supprimer la taille. Veuillez réessayer plus tard."
        );
      }
    }
  };

  // Trouver le nom du produit en fonction de l'ID
  const getProductName = (productId) => {
    // S'assurer que products est un tableau avant d'utiliser find
    if (!Array.isArray(products)) {
      return "Produit inconnu";
    }

    const product = products.find((p) => p.id === productId);
    return product ? product.nom : "Produit inconnu";
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Calculer les éléments à afficher sur la page actuelle
  // Filtrer les tailles selon le terme de recherche
  const filteredSizes = sizes.filter((size) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      size.name.toLowerCase().includes(searchLower) ||
      size.label.toLowerCase().includes(searchLower) ||
      size.price.toString().includes(searchLower) ||
      getProductName(size.product_id).toLowerCase().includes(searchLower)
    );
  });

  // Calculer les éléments à afficher sur la page actuelle
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSizes.slice(indexOfFirstItem, indexOfLastItem);

  // Gérer le changement du nombre d'éléments par page
  const handleItemsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1); // Réinitialiser à la première page
    setTotalPages(Math.ceil(sizes.length / value));
  };

  // Générer les numéros de page à afficher avec ellipses
  const getPageNumbers = () => {
    const pageNumbers = [];

    if (totalPages <= 7) {
      // Si moins de 7 pages, montrer toutes les pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Toujours montrer la première page
      pageNumbers.push(1);

      // Logique pour montrer les ellipses et les pages autour de la page courante
      if (currentPage <= 3) {
        // Si on est proche du début
        pageNumbers.push(2, 3, 4, 5);
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Si on est proche de la fin
        pageNumbers.push("...");
        pageNumbers.push(
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        // Au milieu
        pageNumbers.push("...");
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Formater le nombre d'éléments total
  const formatTotalItems = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M éléments`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K éléments`;
    } else {
      return `${count} éléments`;
    }
  };

  // Vérifier si des produits sont disponibles pour activer/désactiver le bouton
  const areProductsAvailable = Array.isArray(products) && products.length > 0;

  // Helper function to format price by removing trailing zeros
  const formatPrice = (price) => {
    // Convert to string first
    const priceStr = price.toString();

    // If it's not a valid number, return as is
    if (isNaN(parseFloat(priceStr))) {
      return priceStr;
    }

    // Parse as number to remove trailing zeros, then convert back to string
    const numPrice = parseFloat(priceStr);

    // If it's a whole number, add .00
    if (numPrice % 1 === 0) {
      return numPrice.toString() + '.00';
    }

    // For decimal numbers, parseFloat automatically removes trailing zeros
    return numPrice.toString();
  };



  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-20 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="relative py-4 md:py-8">
        <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 ml-8">

          {/* Header Section */}
          <div className="mb-8">
            <div className="bg-white/80 backdrop-blur-lg  pr-6 rounded-3xl shadow-xl border border-white/20 p-4 md:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex items-center space-x-3 md:space-x-4 ">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl md:rounded-3xl flex items-center justify-center">
                    <Ruler className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl md:text-3xl font-bold text-gray-900">Size Management</h2>
                    <p className="text-sm md:text-base text-gray-600">Manage product sizes and pricing</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {/* Search Bar */}
                  <div className="relative w-full sm:w-auto">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search sizes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                    />
                  </div>

                  {/* Add Size Button */}
                  <button
                    onClick={() => openModal()}
                    disabled={!areProductsAvailable}
                    className={`group relative px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${areProductsAvailable
                      ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                      : "bg-gray-400 cursor-not-allowed text-gray-200"
                      }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Plus className="w-5 h-5" />
                      <span className="hidden sm:inline">Add Size</span>
                      <span className="sm:hidden">Add</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8 mb-8">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Error</h3>
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-12">
              <div className="flex flex-col items-center space-y-6">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center animate-pulse">
                  <RefreshCw className="w-10 h-10 text-white animate-spin" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Sizes</h3>
                  <p className="text-gray-600">Please wait while we fetch your size catalog...</p>
                </div>
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce animation-delay-200"></div>
                  <div className="w-3 h-3 bg-teal-500 rounded-full animate-bounce animation-delay-400"></div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Sizes Table */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Table Header */}
                <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 px-8 py-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Ruler className="w-6 h-6 text-white" />
                      <h3 className="text-xl font-bold text-white">Size Catalog</h3>
                      <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {filteredSizes.length} {searchTerm && `/ ${sizes.length}`} sizes
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 text-white/80">
                        <Activity className="w-4 h-4" />
                        <span className="text-sm">Live Data</span>
                      </div>
                      <button
                        onClick={() => window.location.reload()}
                        className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-6 font-semibold text-gray-900">
                          <div className="flex items-center space-x-2">
                            <Tag className="w-4 h-4" />
                            <span>Name</span>
                          </div>
                        </th>
                        <th className="text-left p-6 font-semibold text-gray-900">
                          <div className="flex items-center space-x-2">
                            <Layers className="w-4 h-4" />
                            <span>Label</span>
                          </div>
                        </th>
                        <th className="text-left p-6 font-semibold text-gray-900">
                          <div className="flex items-center space-x-2">
                            <DollarSign className="w-4 h-4" />
                            <span>Price</span>
                          </div>
                        </th>
                        <th className="text-left p-6 font-semibold text-gray-900">
                          <div className="flex items-center space-x-2">
                            <Package className="w-4 h-4" />
                            <span>Product</span>
                          </div>
                        </th>
                        <th className="text-left p-6 font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-12 text-center">
                            <div className="flex flex-col items-center space-y-4">
                              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                <Ruler className="w-8 h-8 text-gray-400" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">No Sizes Found</h3>
                                <p className="text-gray-600">Start by adding your first size</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((size, index) => (
                          <tr
                            key={size.id}
                            className="group border-t border-gray-100 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-300"
                            style={{
                              animationDelay: `${index * 100}ms`
                            }}
                          >
                            <td className="p-6">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                                  <Tag className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-300">
                                    {size.name}
                                  </h4>
                                  <p className="text-sm text-gray-500">ID: {size.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-6">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                <Layers className="w-4 h-4 mr-1" />
                                {size.label}
                              </span>
                            </td>
                            <td className="p-6">
                              <div className="flex items-center space-x-2">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                <span className="font-semibold text-green-600">${formatPrice(size.price)}</span>
                              </div>
                            </td>
                            <td className="p-6">
                              <div className="flex items-center space-x-2">
                                <Package className="w-4 h-4 text-blue-600" />
                                <span className="text-gray-700">{getProductName(size.product_id)}</span>
                              </div>
                            </td>
                            <td className="p-6">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openModal(size)}
                                  className="group relative w-10 h-10 bg-green-100 hover:bg-green-200 text-green-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(size.id)}
                                  className="group relative w-10 h-10 bg-red-100 hover:bg-red-200 text-red-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                                <button className="group relative w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {currentItems.length === 0 ? (
                  <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-12">
                    <div className="flex flex-col items-center space-y-6">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                        <Ruler className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">No Sizes Found</h3>
                        <p className="text-gray-600">Start by adding your first size</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  currentItems.map((size, index) => (
                    <div
                      key={size.id}
                      className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-6 group hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
                      style={{
                        animationDelay: `${index * 150}ms`
                      }}
                    >
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                          <Tag className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-700 transition-colors duration-300">
                            {size.name}
                          </h3>
                          <p className="text-sm text-gray-500">ID: {size.id}</p>
                        </div>
                        <div className="text-right">
                          <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                            ${formatPrice(size.price)}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-blue-50 rounded-2xl p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Layers className="w-4 h-4 text-blue-600" />
                            <span className="text-xs font-medium text-blue-700">Label</span>
                          </div>
                          <span className="text-sm font-bold text-blue-600">{size.label}</span>
                        </div>

                        <div className="bg-green-50 rounded-2xl p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Package className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-medium text-green-700">Product</span>
                          </div>
                          <span className="text-sm font-bold text-green-600 truncate">{getProductName(size.product_id)}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openModal(size)}
                          className="flex-1 bg-green-100 hover:bg-green-200 text-green-600 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(size.id)}
                          className="flex-1 bg-red-100 hover:bg-red-200 text-red-600 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                        >
                          <Trash className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {sizes.length > 0 && (
                <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden mt-8">
                  <div className="bg-gray-50 px-4 md:px-8 py-4 md:py-6 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                        <span className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                          Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to{" "}
                          <span className="font-semibold">{Math.min(indexOfLastItem, filteredSizes.length)}</span> of{" "}
                          <span className="font-semibold">{filteredSizes.length}</span> sizes
                          {searchTerm && <span className="text-green-600"> (filtered)</span>}
                        </span>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="border border-gray-200 rounded-xl px-3 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="5">5 per page</option>
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0">
                        <button
                          onClick={prevPage}
                          disabled={currentPage === 1}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300"
                        >
                          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        {getPageNumbers().map((number, index) =>
                          number === "..." ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="px-2 py-1 text-gray-500 text-sm"
                            >
                              ...
                            </span>
                          ) : (
                            <button
                              key={number}
                              onClick={() => paginate(number)}
                              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base ${currentPage === number
                                ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg"
                                : "bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
                                }`}
                            >
                              {number}
                            </button>
                          )
                        )}
                        <button
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300"
                        >
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* Modern Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 max-w-md w-full">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                    <Ruler className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedSize ? "Edit Size" : "Add Size"}
                    </h2>
                    <p className="text-gray-600">Manage size information</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Size Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                    placeholder="Enter size name"
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{formErrors.name}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Label
                  </label>
                  <input
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                    placeholder="Enter size label"
                  />
                  {formErrors.label && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{formErrors.label}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Price
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="any"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                      placeholder="0.00"
                    />
                  </div>
                  {formErrors.price && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{formErrors.price}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Product
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      name="product_id"
                      value={formData.product_id}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 appearance-none"
                    >
                      {Array.isArray(products) && products.length > 0 ? (
                        products.map((product) => (
                          <option key={product.id} value={product.id.toString()}>
                            {product.nom}
                          </option>
                        ))
                      ) : (
                        <option value="">No products available</option>
                      )}
                    </select>
                  </div>
                  {formErrors.product_id && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{formErrors.product_id}</span>
                    </p>
                  )}
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    {selectedSize ? "Update Size" : "Create Size"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default SizesManagement;